import debounce from "debounce";
import { Observable, Subject } from "rxjs";

import { MOIDefinition } from "./types";

const DEBOUNCE_TIME = 200;

class MutationObserverInjector {
    private readonly mo: MutationObserver;
    private readonly targetsMap = new Map<
        MOIDefinition,
        Subject<HTMLElement>
    >();
    private readonly foundElementsMap = new Map<
        MOIDefinition,
        WeakSet<HTMLElement>
    >();

    constructor(
        MutationObserverCls: typeof MutationObserver,
        mutationTarget: HTMLElement = document.body
    ) {
        this.mo = new MutationObserverCls(this.debouncedMutationCallback);

        this.mo.observe(mutationTarget, {
            attributes: false,
            childList: true,
            subtree: true,
        });
    }

    public register(definition: MOIDefinition): Observable<HTMLElement> {
        if (!this.targetsMap.has(definition)) {
            this.targetsMap.set(definition, new Subject<HTMLElement>());
            this.foundElementsMap.set(definition, new WeakSet<HTMLElement>());
        }

        return new Observable<HTMLElement>((subscriber) => {
            const targets$ = this.targetsMap.get(definition);
            const subscription = targets$?.subscribe(subscriber);

            this.findElement(definition);

            return () => {
                subscription?.unsubscribe();
            };
        });
    }

    public unregister(definition: MOIDefinition): void {
        this.targetsMap.delete(definition);
        this.foundElementsMap.delete(definition);
    }

    private findElement(definition: MOIDefinition): void {
        const elements = document.querySelectorAll<HTMLElement>(
            definition.targetSelector
        );

        for (const element of elements) {
            const foundElements = this.foundElementsMap.get(definition);
            if (foundElements?.has(element)) continue;
            foundElements?.add(element);

            const targets$ = this.targetsMap.get(definition);
            setTimeout(() => {
                targets$?.next(element);
            });
        }
    }

    private findAllElements(): void {
        this.targetsMap.forEach((_, definition) => {
            this.findElement(definition);
        });
    }

    private debouncedMutationCallback = debounce(() => {
        this.findAllElements();
    }, DEBOUNCE_TIME);
}

export default MutationObserverInjector;
